import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import CreateEditForm from "../../molecules/createEditForm/createEditForm";
import Skeleton from "react-loading-skeleton";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {RoutePaths} from "../../../shared/routePaths";
import {useHistory} from "react-router";
import {Navigation} from "../../../utils/navigation";
import {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import {Col, Row} from "reactstrap";
import DateEdit from "../../organisms/dateEdit/dateEdit";
import {TimeZones} from "../../../shared/enums/timeZones";
import TextEdit from "../../organisms/textEdit/textEdit";
import MultiVillageSelect from "../../molecules/multiVillageSelect/multiVillageSelect";
import CheckBox from "../../atoms/checkBox/checkBox";
import "./specialDaysCreateEditTemplate.sass";
import Occasion from "../../../stores/domainStore/pages/models/occasion";
import {useStores} from "../../../stores/useStores";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import {Action, Path} from "history";
import {format} from "date-fns";

/**
 * Properties that define the SpecialDaysCreateEditTemplate.
 */
export interface SpecialDaysCreateEditTemplateProps {
  onPublish: (payload: Occasion) => Promise<void>;
  specialDayId?: string;
  deleteSpecialDay?: (
    specialDayId: string,
    specialDayTitle: string
  ) => Promise<void>;
}

/**
 * The template for create or edit a special day template.
 */

const SpecialDaysCreateEditTemplate: React.FC<
  SpecialDaysCreateEditTemplateProps
> = ({onPublish, specialDayId, deleteSpecialDay}) => {
  const history = useHistory();
  const {t} = useTranslation(["translation", "specialDays"]);
  const {
    uiStore: {modalStore, toastMessageStore},
    domainStore: {occasionDomainStore},
    userService
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [occasionInput, setOccasionInput] = useState<Occasion>(new Occasion());
  const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();

    const getOccasion = async (occasionId: string) =>
      await occasionDomainStore.getOccasion(occasionId, controller);

    if (specialDayId) {
      getOccasion(specialDayId)
        .then((occasion) => {
          setOccasionInput(occasion);
          setIsLoading(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          toastMessageStore.displayToastMessage(
            t("specialDays:messages.invalidGettingSpecialDay"),
            ToastMessageStatus.Danger
          );
          Navigation.go(history, RoutePaths.SpecialDays);
        });
    } else {
      setIsLoading(false);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialDayId]);

  useEffect(() => {
    const unblock = history.block((path: Path, action: Action) => {
      if (occasionInput.hasChanges) {
        // If the modal is open and the user hit the back button on the browser, unblock the navigation.
        if (modalStore.isOpen && action === Action.Pop) {
          unblock();
          return true;
        }
        modalStore.openModalWithContent({
          content: t("translation:messages.cancelCreateOrEdit", {
            target: t("specialDays:specialDay")
          }),
          title: t("translation:titles.unsavedChanges"),
          primaryButtonText: t("translation:confirmations.affirmativeLeave"),
          secondaryButtonText: t("translation:confirmations.negative"),
          canToggle: false,
          dangerButton: true,
          centered: true,
          onPrimaryClick: () => {
            unblock();
            history.push(path);
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        return false;
      }
    });
    return () => {
      unblock();
    };
  }, [occasionInput.hasChanges, history, modalStore, t]);

  const timeZone =
    userService?.getSelectedVillage()?.timeZone ?? TimeZones.Auckland;

  const pageTitle = specialDayId
    ? occasionInput.title.text
    : t("specialDays:titles.create");

  const breadcrumbItem: BreadcrumbItem = {
    name: t("specialDays:titles.specialDay"),
    onClick: () => Navigation.go(history, RoutePaths.SpecialDays),
    child: {name: pageTitle}
  };

  const performAction = async (action: () => Promise<void>) => {
    setIsPublishing(true);
    try {
      occasionInput.setIsDirty(true);
      if (occasionInput.canPublish) {
        await action();
        occasionInput.setIsDirty(false);
        Navigation.go(history, RoutePaths.SpecialDays);
      } else {
        setIsPublishing(false);
      }
    } catch {
      setIsPublishing(false);
      setDeleteConfirmed(false);
    }
  };

  const submitAction = async () => {
    await performAction(async () => await onPublish(occasionInput));
  };

  const deleteAction = () => {
    if (specialDayId && deleteSpecialDay) {
      modalStore.openModalWithContent({
        content: t("specialDays:messages.deleteNotice"),
        title: t("specialDays:titles.deleteNotice"),
        primaryButtonText: t("translation:confirmations.affirmativeDelete"),
        primaryButtonTestId: "specialDayDeleteConfirmButton",
        centered: true,
        dangerButton: true,
        onPrimaryClick: () => {
          setDeleteConfirmed(true);
          modalStore.toggle();
        }
      });
    }
  };

  useEffect(() => {
    if (deleteConfirmed && specialDayId && deleteSpecialDay) {
      (async () =>
        await performAction(
          async () =>
            await deleteSpecialDay(specialDayId, occasionInput.title.text)
        ))();
    }
  }, [deleteConfirmed]);

  return (
    <div className="special-day-template">
      <CreateEditForm>
        <CreateEditForm.Header
          hasUnsavedChanges={occasionInput.hasChanges}
          breadcrumbItem={breadcrumbItem}
        >
          <div className="special-days-action-buttons">
            {specialDayId && deleteSpecialDay && (
              <ButtonIcon
                type="secondary-negative"
                testId="specialDayDeleteButton"
                onClick={deleteAction}
                isDisabled={isPublishing || !occasionInput.canPublish}
                isBusy={isPublishing}
              >
                {t("specialDays:actions.delete")}
              </ButtonIcon>
            )}
            <ButtonIcon
              type="primary"
              onClick={submitAction}
              isDisabled={isPublishing || !occasionInput.canPublish}
              isBusy={isPublishing}
            >
              {t("translation:actions.publish")}
            </ButtonIcon>
          </div>
        </CreateEditForm.Header>
        <CreateEditForm.Body>
          {isLoading ? (
            <Skeleton className="placeholder" />
          ) : (
            <>
              {specialDayId && (
                <Row className="row-with-margin">
                  <Col>
                    <div className="special-day-info">
                      <span className="special-day-title">{pageTitle}</span>
                      <span className="special-day-date">
                        {format(occasionInput.date.date!, "dd MMMM")}
                      </span>
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="row-with-margin">
                <Col>
                  <DateEdit
                    label={t("specialDays:labels:date")}
                    overrideIsRequired={true}
                    value={occasionInput.date}
                    dateFormat={"dd/MM/yyyy"}
                    timeZone={timeZone}
                    placeholder={t("specialDays:placeholders:date")}
                    showTimeSelect={false}
                    validationMessage={t("specialDays:errors:date")}
                    position={"bottom"}
                  />
                </Col>
                <Col>
                  <TextEdit
                    type={"text"}
                    value={occasionInput.title}
                    label={t("specialDays:labels:title")}
                    placeholder={t("specialDays:placeholders:title")}
                    validationMessage={t("specialDays:errors:title")}
                  />
                </Col>
              </Row>
              <Row className="row-with-margin">
                <Col>
                  <MultiVillageSelect
                    villages={occasionInput.villageIds}
                    placeholder={t("specialDays:placeholders:village")}
                    invalidMinSelectedVillageErrorMessage={t(
                      "specialDays:errors:village"
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="pb-4">
                  <CheckBox
                    value={occasionInput.isPrimary}
                    onValueChange={(value) =>
                      occasionInput.isPrimary.setBoolean(value)
                    }
                    label={t("specialDays:labels.primarySpecialDay")}
                  />
                </Col>
                <Col />
              </Row>
              <Row className="form-separator row-with-margin" />
              <Row>
                <Col className="pb-4">
                  <div className="special-day-catering">
                    <label>
                      <span className="label">
                        {t("specialDays:labels.cateringRequirements")}
                      </span>
                      <span>
                        <small>
                          {t("specialDays:helperTexts.cateringRequirements")}
                        </small>
                      </span>
                    </label>
                    <CheckBox
                      value={occasionInput.hasFood}
                      onValueChange={(value) =>
                        occasionInput.hasFood.setBoolean(value)
                      }
                      label={t("specialDays:labels.food")}
                    />
                    <CheckBox
                      value={occasionInput.hasDrink}
                      onValueChange={(value) =>
                        occasionInput.hasDrink.setBoolean(value)
                      }
                      label={t("specialDays:labels.beverages")}
                    />
                  </div>
                </Col>
                <Col />
              </Row>
            </>
          )}
        </CreateEditForm.Body>
      </CreateEditForm>
    </div>
  );
};

export default observer(SpecialDaysCreateEditTemplate);
